import { apiClient } from './client'

// Справочник поставщиков (Р12, файл РОЗ «Условия/Объём CNY»).

export type SupplierType = 'MANUFACTURER' | 'DISTRIBUTOR' | 'AGENT' | 'PLATFORM'
export type SupplierPaymentType =
  | 'PREPAY_100'
  | 'PREPAY_50_50'
  | 'PREPAY_30_70'
  | 'AFTERPAY_CREDIT_LINE'
  | 'PLACEMENT_LIMIT'
  | 'MIXED_THRESHOLD'
  | 'NEGOTIABLE'

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  MANUFACTURER: 'Производитель',
  DISTRIBUTOR: 'Дистрибьютор',
  AGENT: 'Агент/трейдер',
  PLATFORM: 'Площадка',
}

export const SUPPLIER_PAYMENT_LABELS: Record<SupplierPaymentType, string> = {
  PREPAY_100: '100% предоплата',
  PREPAY_50_50: 'Предоплата 50/50',
  PREPAY_30_70: 'Предоплата 30/70',
  AFTERPAY_CREDIT_LINE: 'Постоплата (кредитная линия)',
  PLACEMENT_LIMIT: 'Лимит размещения',
  MIXED_THRESHOLD: 'Смешанные (порог по сумме)',
  NEGOTIABLE: 'Договорные',
}

export interface SupplierAlias {
  id: string
  aliasName: string
  aliasKey: string
}

export interface Supplier {
  id: string
  nameKey: string
  name: string
  type: SupplierType | null
  responsibleBuyerId: string | null
  responsibleBuyer: { id: string; name: string | null; email: string } | null
  paymentType: SupplierPaymentType | null
  creditLimitAmount: string | null
  creditLimitCurrency: string | null
  postShipmentDays: number | null
  warrantyDays: number | null
  complaintsCount: number
  complaintsNotes: string | null
  paymentNotes: string | null
  /** Ручной рейтинг надёжности 1–5 (null — не оценён). */
  rating: number | null
  /** Скрыт из справочника (soft, чистка мусора); null — активен. */
  hiddenAt: string | null
  createdAt: string
  updatedAt: string
  /** Имена-дубли, объединённые в этого поставщика (WTR ← Wtr, Wtron). */
  aliases: SupplierAlias[]
  /** Записей в журнале рекламаций (суммарный счётчик = complaintsCount + это). */
  _count?: { complaints: number }
}

/**
 * Поток поставщика по матрицам его квот: IMPORT — «Китай» / «Производители» /
 * «Стоки», RF — только «РФ» / «Склад», MIXED — и то и другое, NONE — квот нет.
 */
export type SupplierStream = 'IMPORT' | 'RF' | 'MIXED' | 'NONE'

/**
 * Что текущий пользователь может сделать с поставщиком — присылает сервер
 * (suppliers.access.ts), кнопки правки показываем только по этим флагам.
 */
export interface SupplierPermissions {
  /** Имя и условия. */
  edit: boolean
  /** Рейтинг и ответственный закупщик. */
  editRatingAndBuyer: boolean
  /** Скрыть из справочника и вернуть. */
  hide: boolean
  /** Участвовать в объединении дублей. */
  merge: boolean
  /** Записать случай в журнал рекламаций. */
  complain: boolean
}

/** Строка списка: поставщик + счётчики работы по всем его именам. */
export interface SupplierListRow extends Supplier {
  /** Квот дал (с ценой; по nameKey + алиасам). */
  quotesGiven: number
  /** Позиций ушло в счёт с его квотой. */
  wonItems: number
  stream: SupplierStream
  permissions: SupplierPermissions
}

// ── Карточка поставщика + журнал рекламаций (23.07) ──

export type SupplierComplaintKind = 'COUNTERFEIT' | 'SHORTAGE' | 'QUALITY' | 'DELAY' | 'OTHER'

export const COMPLAINT_KIND_LABELS: Record<SupplierComplaintKind, string> = {
  COUNTERFEIT: 'Контрафакт',
  SHORTAGE: 'Недогруз',
  QUALITY: 'Качество',
  DELAY: 'Срыв сроков',
  OTHER: 'Другое',
}

export interface SupplierComplaint {
  id: string
  kind: SupplierComplaintKind
  description: string
  requestNumber: string | null
  partNumber: string | null
  createdAt: string
  createdBy: { id: string; name: string | null; email: string } | null
  /** Сервер разрешит удалить запись: автор, РОЗ или админ. */
  canDelete: boolean
}

export interface SupplierCard {
  supplier: Supplier & { complaints: SupplierComplaint[] }
  stream: SupplierStream
  permissions: SupplierPermissions
  stats: {
    bomRequests: number
    itemsRequested: number
    quotesGiven: number
    quotesPriced: number
    wonItems: number
    requestsInvolved: number
  }
  topBrands: Array<{ name: string; quotes: number; wins: number }>
  topCategories: Array<{ name: string; quotes: number; wins: number }>
  recentRequests: Array<{
    requestId: string
    requestNumber: string | null
    clientName: string
    createdAt: string
    quotes: number
    wins: number
  }>
}

export async function getSupplierCard(id: string): Promise<SupplierCard> {
  const res = await apiClient.get<{ success: true; data: SupplierCard }>(`/suppliers/${id}/card`)
  return res.data.data
}

export async function addSupplierComplaint(
  supplierId: string,
  body: {
    kind: SupplierComplaintKind
    description: string
    requestNumber?: string | null
    partNumber?: string | null
  },
): Promise<SupplierComplaint> {
  const res = await apiClient.post<{ success: true; data: SupplierComplaint }>(
    `/suppliers/${supplierId}/complaints`,
    body,
  )
  return res.data.data
}

export async function deleteSupplierComplaint(complaintId: string): Promise<void> {
  await apiClient.delete(`/suppliers/complaints/${complaintId}`)
}

export interface SupplierBody {
  name: string
  type?: SupplierType | null
  responsibleBuyerId?: string | null
  paymentType?: SupplierPaymentType | null
  creditLimitAmount?: number | null
  creditLimitCurrency?: string | null
  postShipmentDays?: number | null
  warrantyDays?: number | null
  complaintsCount?: number
  complaintsNotes?: string | null
  paymentNotes?: string | null
  rating?: number | null
}

export interface SuppliersListResponse {
  suppliers: SupplierListRow[]
  /** Сколько поставщиков скрыто (для тумблера «Скрытые (N)»). */
  hiddenCount: number
}

export async function getSuppliers(opts?: {
  search?: string
  showHidden?: boolean
}): Promise<SuppliersListResponse> {
  const params: Record<string, string> = {}
  if (opts?.search) params.search = opts.search
  if (opts?.showHidden) params.showHidden = '1'
  const res = await apiClient.get<{ success: true; data: SuppliersListResponse }>('/suppliers', {
    params: Object.keys(params).length ? params : undefined,
  })
  return res.data.data
}

/** Возврат скрытого поставщика в справочник. */
export async function restoreSupplier(id: string): Promise<void> {
  await apiClient.post(`/suppliers/${id}/restore`)
}

/** Объединение дублей: sources становятся алиасами target (рекламации переезжают). */
export async function mergeSuppliers(targetId: string, sourceIds: string[]): Promise<void> {
  await apiClient.post('/suppliers/merge', { targetId, sourceIds })
}

/** Поставщик из справочника, похожий на вводимое имя (подсказка дублей, C3). */
export interface SimilarSupplier {
  id: string
  /** Основное имя в справочнике. */
  name: string
  /**
   * exact — это имя уже есть; alias — это второе имя поставщика после
   * слияния (в viaAlias); similar — похожее, возможно, тот же поставщик.
   */
  match: 'exact' | 'alias' | 'similar'
  viaAlias: string | null
}

export async function getSimilarSuppliers(
  name: string,
  excludeId?: string,
): Promise<SimilarSupplier[]> {
  const res = await apiClient.get<{ success: true; data: SimilarSupplier[] }>(
    '/suppliers/similar',
    { params: { name, ...(excludeId ? { excludeId } : {}) } },
  )
  return res.data.data
}

export interface SupplierBuyer {
  id: string
  name: string | null
  email: string
  role: 'PROCUREMENT' | 'ROS'
}

export async function getSupplierBuyers(): Promise<SupplierBuyer[]> {
  const res = await apiClient.get<{ success: true; data: SupplierBuyer[] }>('/suppliers/buyers')
  return res.data.data
}

export async function createSupplier(body: SupplierBody): Promise<Supplier> {
  const res = await apiClient.post<{ success: true; data: Supplier }>('/suppliers', body)
  return res.data.data
}

export async function updateSupplier(id: string, body: Partial<SupplierBody>): Promise<Supplier> {
  const res = await apiClient.patch<{ success: true; data: Supplier }>(`/suppliers/${id}`, body)
  return res.data.data
}

export async function deleteSupplier(id: string): Promise<void> {
  await apiClient.delete(`/suppliers/${id}`)
}

export interface SupplierImportRow extends SupplierBody {
  responsibleBuyerName?: string | null
}

export async function importSuppliers(
  rows: SupplierImportRow[],
): Promise<{ created: number; updated: number; unknownBuyers: string[] }> {
  const res = await apiClient.post<{
    success: true
    data: { created: number; updated: number; unknownBuyers: string[] }
  }>('/suppliers/import', { rows })
  return res.data.data
}
